import { StringInfo } from '../types';

export type ChangeType = 'added' | 'removed' | 'changed';

export interface DiffResult {
  stringId: string;
  changeType: ChangeType;
  previous?: StringInfo;
  current?: StringInfo;
}

/**
 * Computes the diff between two versions of a string group's strings.
 * Comparison uses stringId as the stable key and considers all source fields and CX variants.
 */
export function computeVersionDiff(
  oldStrings: StringInfo[],
  newStrings: StringInfo[],
): DiffResult[] {
  const oldMap = new Map(oldStrings.map((s) => [s.stringId, s]));
  const newMap = new Map(newStrings.map((s) => [s.stringId, s]));
  const results: DiffResult[] = [];

  // Added or changed strings (present in new)
  for (const [stringId, newString] of newMap) {
    const oldString = oldMap.get(stringId);
    if (!oldString) {
      results.push({ stringId, changeType: 'added', current: newString });
    } else if (!areStringsEqual(oldString, newString)) {
      results.push({
        stringId,
        changeType: 'changed',
        previous: oldString,
        current: newString,
      });
    }
  }

  // Removed strings (present in old but not new)
  for (const [stringId, oldString] of oldMap) {
    if (!newMap.has(stringId)) {
      results.push({ stringId, changeType: 'removed', previous: oldString });
    }
  }

  return results;
}

function areStringsEqual(left: StringInfo, right: StringInfo): boolean {
  if (
    left.sourceLocale !== right.sourceLocale ||
    left.sourceText !== right.sourceText ||
    (left.contextNote ?? '') !== (right.contextNote ?? '') ||
    (left.inlineMarkupType ?? '') !== (right.inlineMarkupType ?? '') ||
    (left.messageFormatType ?? '') !== (right.messageFormatType ?? '')
  ) {
    return false;
  }

  const leftVariants = [...left.variants].sort((a, b) =>
    a.cxId.localeCompare(b.cxId),
  );
  const rightVariants = [...right.variants].sort((a, b) =>
    a.cxId.localeCompare(b.cxId),
  );
  return (
    leftVariants.length === rightVariants.length &&
    leftVariants.every((variant, index) => {
      const other = rightVariants[index];
      return (
        variant.cxId === other.cxId &&
        variant.sourceText === other.sourceText &&
        (variant.contextNote ?? '') === (other.contextNote ?? '')
      );
    })
  );
}
